<script setup lang="ts">
interface AllergenGroup {
  name: string;
  abbreviation: string;
  image: string;
  toggle?: string;
  list?: string[];
  is_active: boolean;
}

interface TemperatureOption {
  name: string;
  value: boolean;
}

interface Props {
  allergens?: Record<string, unknown>[] | boolean;
  results?: number;
}

const props = withDefaults(defineProps<Props>(), {
  allergens: false,
  results: 0,
});

const emit = defineEmits<{
  returnAllergens: [selected: Record<string, unknown>[]];
  returnTemperature: [value: boolean | null];
  returnShowAllergens: [show: boolean];
}>();

const selected = ref<Record<string, unknown>[]>([]);
const hovered = ref<number | false>(false);
const showAllergens = ref(false);

const temperatures: TemperatureOption[] = [
  { name: "Hot", value: true },
  { name: "Cold", value: false },
];

// Dietary chips read the product's raw claim flag, the same one ProductAllergens.vue
// badges; the Allergens chips below (`list`) exclude one tagged allergen each, and an
// untagged dish passes them only on the maker's "no known allergens" declaration. A
// bundle passes no chip. The flags are trusted because input is validated front and
// back: hub's edit page locks a claim the tags contradict, and the backend switches one
// off on every save. Rows saved before that are found with
// GET /api/users/products/checkConflict and cleaned up.
const allergenGroups: AllergenGroup[] = [
  {
    name: "Vegetarian",
    abbreviation: "V",
    image: "veggie",
    toggle: "is_vegetarian",
    is_active: true,
  },
  {
    name: "Vegan",
    abbreviation: "VE",
    image: "vegan",
    toggle: "is_vegan",
    is_active: true,
  },
  {
    name: "No Gluten",
    abbreviation: "NG",
    image: "gluten-free",
    toggle: "is_gluten_free",
    is_active: true,
  },
  {
    name: "No Nuts",
    abbreviation: "NN",
    image: "nut-free",
    toggle: "is_nut_free",
    is_active: true,
  },
  {
    name: "No Dairy",
    abbreviation: "ND",
    image: "dairy-free",
    toggle: "is_dairy_free",
    is_active: true,
  },
  {
    name: "Halal",
    abbreviation: "H",
    image: "halal",
    toggle: "is_halal",
    is_active: true,
  },
];

watch(showAllergens, (to) => {
  emit("returnShowAllergens", to);
});

function addAllergen(allergen: Record<string, unknown>) {
  const hasAllergen = selected.value.find(
    (item: Record<string, unknown>) => item.name === allergen.name,
  );
  if (hasAllergen) {
    selected.value.splice(selected.value.indexOf(hasAllergen), 1);
  } else {
    selected.value.push(allergen);
  }
  emit("returnAllergens", selected.value);
}

function applyFilters() {
  showAllergens.value = false;
}

function clearSelections() {
  selected.value = [];
  emit("returnAllergens", selected.value);
  emit("returnTemperature", null);
}

function addTemperatureFilter(temperature: TemperatureOption) {
  const hasTemperature = selected.value.find(
    (item: Record<string, unknown>) => item.name === temperature.name,
  );
  if (hasTemperature) {
    selected.value = selected.value.filter(
      (item: Record<string, unknown>) => item.name !== temperature.name,
    );
    emit("returnTemperature", null);
  } else {
    selected.value = selected.value.filter(
      (item: Record<string, unknown>) =>
        item.name !== "Hot" && item.name !== "Cold",
    );
    selected.value.push(temperature as unknown as Record<string, unknown>);
    emit("returnTemperature", temperature.value);
  }
}
</script>

<template>
  <div class="allergen-filter">
    <div
      class="allergen-filter__button"
      :class="selected.length > 0 && 'selected-allergens'"
      @click="showAllergens = !showAllergens"
    >
      Filters
      <fa :icon="['fal', 'bars']" />
    </div>
    <DynamicModal
      v-if="showAllergens"
      :full-screen="true"
      @close-modal="showAllergens = false"
    >
      <template #body>
        <div class="allergen-filter__modal">
          <h3 class="grey-text">Dietaries</h3>
          <div class="allergen-filter__modal--wrapper">
            <div
              v-for="(allergen, i) in allergenGroups"
              :key="`allergen_${i}`"
              class="item"
              :class="[
                selected.find((item) => item.name === allergen.name) &&
                  `${allergen.image}-selected`,
              ]"
              @mousedown="hovered = i"
              @mouseover="hovered = i"
              @mouseleave="hovered = false"
              @click="addAllergen(allergen as unknown as Record<string, unknown>)"
            >
              <p
                class="abbreviation small-text grey-text"
                :class="allergen.image"
              >
                {{ allergen.abbreviation }}
              </p>
              <p class="name small-text grey-text">
                {{ allergen.name }}
              </p>
            </div>
          </div>
        </div>
        <div class="allergen-filter__modal">
          <h3 class="grey-text">Allergens</h3>
          <div class="allergen-filter__modal--wrapper">
            <div
              v-for="(allergen, i) in (Array.isArray(allergens) ? allergens : [])"
              :key="`allergen_item_${i}`"
              class="item"
              :class="[
                selected.find((item) => item.name === (allergen as Record<string, unknown>).name) &&
                  'allergen-selected',
              ]"
              @mousedown="hovered = i"
              @mouseover="hovered = i"
              @mouseleave="hovered = false"
              @click="
                addAllergen({
                  ...(allergen as Record<string, unknown>),
                  list: [(allergen as Record<string, unknown>).name],
                })
              "
            >
              <p class="name small-text grey-text">
                {{
                  String((allergen as Record<string, unknown>).name || '').charAt(0).toUpperCase() +
                  String((allergen as Record<string, unknown>).name || '').slice(1)
                }}
              </p>
            </div>
          </div>
        </div>
        <div class="allergen-filter__modal">
          <h3 class="grey-text">Temperature</h3>
          <div class="allergen-filter__modal--wrapper">
            <div
              v-for="(temperature, i) in temperatures"
              :key="`temperature_${i}`"
              class="item"
              :class="[
                selected.find((item) => item.name === temperature.name) && 'temperature-selected',
                temperature.name.toLowerCase(),
              ]"
              @mousedown="hovered = i"
              @mouseover="hovered = i"
              @mouseleave="hovered = false"
              @click="addTemperatureFilter(temperature)"
            >
              <p class="abbreviation small-text grey-text">
                {{ temperature.name.charAt(0) }}
              </p>
              <p class="name small-text grey-text">
                {{ temperature.name }}
              </p>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="allergen-filter__modal-actions">
          <a
            class="allergen-filter__clear"
            @click="clearSelections"
          >Clear filters</a>
          <DynamicButton
            color="pink"
            width="50%"
            :text="`Show ${results} results`"
            @click-event="applyFilters"
          />
        </div>
      </template>
    </DynamicModal>
  </div>
</template>

<style lang="scss">
$laptop: 1200px;
$tablet: 768px;
$mobile: 600px;

.allergen-filter {
  display: flex;
  flex-direction: column;
  // Filters button — same treatment as inactive category pills (cream
  // secondary, foreground ink, transparent border). Active state below
  // (.selected-allergens) is the only place the pill turns pink.
  &__button {
    color: var(--color-grey-1) !important;
    border: 0.0625rem solid transparent !important;
    white-space: nowrap;
    padding: 0.5rem 1.2rem;
    border-radius: var(--radius);
    font-family: "GalanoGrotesqueMedium", sans-serif;
    font-size: 0.9rem;
    font-weight: var(--weight-medium);
    background: var(--color-grey-4);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    transition:
      background-color 0.2s ease,
      color 0.2s ease,
      border-color 0.2s ease;
    // Hover wash matches the list-filter category pills + search-bar
    // chip family — muted pink fill with primary pink ink — so all
    // "interactive filter chrome" reads as one family on hover.
    &:hover {
      background: var(--color-pink-4) !important;
      color: var(--color-pink-1) !important;
      border-color: var(--color-pink-4) !important;
    }
    @media (max-width: $tablet) {
      width: auto;
    }
  }
  &__modal {
    &__title {
      text-align: center;
    }
    &--wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: center;
      margin-bottom: 1rem;
      padding-bottom: 1rem;
      border-bottom: 2px solid var(--color-grey-3);
      .item {
        cursor: pointer;
        align-items: center;
        display: flex;
        font-family: "GalanoGrotesqueMedium", sans-serif !important;
        padding: 0rem 0.8rem;
        border: 2px solid var(--color-grey-3);
        margin: 0.5rem 0.5rem 0.5rem 0;
        border-radius: var(--radius);
        &:hover {
          transform: scale(1.02);
        }
        // The icon abbreviation circles inherit the global pastel fills
        // from ProductAllergens.vue (.veggie / .halal / etc.). The outer
        // pill borders below match that pastel palette so the chip border
        // and the icon inside stay visually consistent.
        .abbreviation {
          width: 1.2rem;
          height: 1.2rem;
          margin: 0.2rem 0.5rem 0.2rem 0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.125rem;
        }
        .name {
          font-size: 0.7rem;
          line-height: 0.5rem;
          color: var(--color-grey-1);
        }
      }
      // Selected state: the outer pill border picks up the muted text colour
      // of the matching pastel, so the chip outline and the icon inside agree.
      .allergen-selected {
        border: 0.125rem solid var(--color-pink-1) !important;
      }
      .veggie-selected,
      .vegan-selected {
        border: 0.125rem solid var(--color-leaf-1) !important;
      }
      .gluten-free-selected {
        border: 0.125rem solid var(--color-amber-1) !important;
      }
      .nut-free-selected {
        border: 0.125rem solid var(--color-nut-1) !important;
      }
      .dairy-free-selected {
        border: 0.125rem solid var(--color-wheat-1) !important;
      }
      .halal-selected {
        border: 0.125rem solid var(--color-rose-1) !important;
      }
    }
  }
  &__modal-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
  }

  // Inline link → foreground ink, primary on hover. The global `a` rule
  // ships purple by default; override here so the modal stays on-palette.
  &__clear {
    color: var(--color-grey-1) !important;
    font-weight: var(--weight-semibold);
    font-size: 1rem;
    cursor: pointer;
    text-decoration: none !important;
    transition: color 0.2s ease;
    &:hover {
      color: var(--color-pink-1) !important;
      text-decoration: underline !important;
    }
  }
}

// Temperature chips share the rose/sky muted pastels used everywhere else
// in the audit (Cold pill on product cards, Halal dietary chip).
.hot {
  .abbreviation {
    background-color: var(--color-rose-2);
    color: var(--color-rose-1) !important;
  }
}

.cold {
  .abbreviation {
    background-color: var(--color-sky-2);
    color: var(--color-sky-1) !important;
  }
}

.temperature-selected.hot {
  border: 0.125rem solid var(--color-rose-1) !important;
}

.temperature-selected.cold {
  border: 0.125rem solid var(--color-sky-1) !important;
}

.item.hot:hover {
  transform: scale(1.02);
}

.item.cold:hover {
  transform: scale(1.02);
}

.selected-allergens {
  background-color: var(--color-pink-1);
  color: var(--color-white-1) !important;
  border: 2px solid var(--color-pink-1) !important;
}
</style>
